import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Delete, ShieldCheck, ArrowRight, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import { setActiveTimeZone } from "@/lib/datetime";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import parknovaLogo from "@/assets/brand/parknova-logo.png";

export default function Login() {
  const { loginAdmin, loginOperativo } = useAuth();
  const navigate = useNavigate();

  // Admin state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);

  // Operativo state
  const [searchParams] = useSearchParams();
  const codigoParam = searchParams.get("codigo") || "";
  const sucursalParam = searchParams.get("sucursal") || "";
  const [codigoEmpresa, setCodigoEmpresa] = useState("");
  const [usuarioCorto, setUsuarioCorto] = useState("");
  const [nip, setNip] = useState("");
  const [opLoading, setOpLoading] = useState(false);

  // Persistencia SOLO del código de empresa (lado operativo). Usuario y NIP nunca se guardan.
  const CODIGO_KEY = "pap_codigo_empresa";

  useEffect(() => {
    if (codigoParam) { setCodigoEmpresa(codigoParam.toUpperCase()); return; }
    const saved = localStorage.getItem(CODIGO_KEY);
    if (saved) setCodigoEmpresa(saved.toUpperCase());
  }, [codigoParam]);

  const handleAdmin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    try {
      await loginAdmin(email.trim().toLowerCase(), password);
      toast.success("Bienvenido");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error de acceso");
    } finally {
      setAdminLoading(false);
    }
  };

  const pressKey = (k) => {
    setNip((prev) => (prev.length < 4 ? prev + k : prev));
  };
  const delKey = () => setNip((prev) => prev.slice(0, -1));

  const handleOperativo = async () => {
    if (!codigoEmpresa.trim() || !usuarioCorto || nip.length !== 4) {
      toast.error("Completa código de empresa, usuario y NIP de 4 dígitos");
      return;
    }
    setOpLoading(true);
    try {
      const codigoNorm = codigoEmpresa.trim().toUpperCase();
      const opUser = await loginOperativo(codigoNorm, usuarioCorto.trim(), nip);
      // Guarda SOLO el código de empresa para próximos ingresos (usuario/NIP nunca se guardan).
      localStorage.setItem(CODIGO_KEY, codigoNorm);
      const operativoHome = opUser?.rol === "Valet" ? "/operativo/valet" : "/operativo";
      if (sucursalParam) {
        // QR scenario: jump straight to the specific branch
        const { data } = await listSucursales();
        const s = data.find((x) => x.id_sucursal === sucursalParam);
        if (!s) {
          toast.error("La sucursal del enlace no pertenece a esta empresa");
          setNip("");
          setOpLoading(false);
          return;
        }
        localStorage.setItem("pap_sucursal", JSON.stringify({ id: s.id_sucursal, nombre: s.nombre, modelo_cobro: s.modelo_cobro || "COMBINADO", zona_horaria: s.zona_horaria || "America/Mexico_City" }));
        setActiveTimeZone(s.zona_horaria || "America/Mexico_City");
        toast.success("Acceso concedido");
        navigate(operativoHome);
      } else {
        // Manual scenario: go to branch selection screen
        localStorage.removeItem("pap_sucursal");
        toast.success("Acceso concedido");
        navigate("/operativo/seleccion");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error de acceso");
      setNip("");
    } finally {
      setOpLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* LEFT — Administrativo (marca ParkNova) */}
      <div className="flex flex-col justify-center items-center px-6 sm:px-12 lg:px-16 py-14 bg-gradient-to-b from-white via-white to-[#F5F8FC]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex flex-col items-center text-center mb-8">
            <img
              src={parknovaLogo}
              alt="ParkNova"
              draggable="false"
              className="h-20 sm:h-24 w-auto max-w-[280px] object-contain mb-6 select-none"
            />
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0B2A4A]">Bienvenido a ParkNova</h1>
            <p className="text-sm sm:text-base text-[#52607A] mt-2">Controla tu operación. Genera más ingresos.</p>
          </div>

          <div className="bg-white border border-[#E6EAF0] rounded-2xl shadow-[0_18px_50px_-20px_rgba(11,42,74,0.28)] p-6 sm:p-8">
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8A94A6] mb-5 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#C99A2E]" /> Acceso administrativo
            </p>
            <form onSubmit={handleAdmin} className="space-y-4" autoComplete="off">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs tracking-[0.12em] uppercase font-bold text-[#52607A]">Correo</Label>
                <Input
                  id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@empresa.com" data-testid="admin-email-input"
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} data-lpignore="true" data-form-type="other"
                  className="h-12 rounded-lg border border-[#D8DFEA] bg-white focus-visible:ring-2 focus-visible:ring-[#0B2A4A] focus-visible:border-[#0B2A4A] focus-visible:ring-offset-0"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs tracking-[0.12em] uppercase font-bold text-[#52607A]">Contraseña</Label>
                <Input
                  id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="admin-password-input"
                  autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck={false} data-lpignore="true" data-form-type="other"
                  className="h-12 rounded-lg border border-[#D8DFEA] bg-white focus-visible:ring-2 focus-visible:ring-[#0B2A4A] focus-visible:border-[#0B2A4A] focus-visible:ring-offset-0"
                />
              </div>
              <button
                type="submit" disabled={adminLoading} data-testid="admin-login-btn"
                className="w-full h-12 rounded-lg bg-[#0B2A4A] text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-colors duration-200 hover:bg-[#0A1F38] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A2E] focus-visible:ring-offset-2 disabled:opacity-60"
              >
                {adminLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Iniciar sesión <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          </div>
          <p className="mt-6 text-xs text-[#8A94A6] text-center">Para roles SuperAdmin y TenantAdmin.</p>
        </motion.div>
      </div>

      {/* RIGHT — Operativo */}
      <div className="relative flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-14 bg-[#0B2A4A] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.12] bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/18927511/pexels-photo-18927511.jpeg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2A4A]/85 to-[#0A1F38]/95" />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative max-w-md w-full mx-auto"
        >
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E7B93E] mb-2">Acceso Operativo</p>
          <h2 className="font-display text-3xl font-black tracking-tight mb-8">Punto de cobro</h2>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/60">Código de Empresa</Label>
              {codigoParam ? (
                <div className="h-11 flex items-center gap-2 px-3 bg-[#E7B93E]/10 border border-[#E7B93E]/40 text-[#E7B93E] font-display font-bold tracking-wider" data-testid="op-codigo-locked">
                  <QrCode className="h-4 w-4" /> {codigoEmpresa}
                </div>
              ) : (
                <Input
                  value={codigoEmpresa} onChange={(e) => setCodigoEmpresa(e.target.value.toUpperCase())}
                  placeholder="DIC-8A2F" data-testid="op-codigo-input" autoComplete="off"
                  className="h-11 rounded-none bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[#E7B93E] uppercase tracking-wider font-display"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/60">Usuario</Label>
              <Input
                value={usuarioCorto} onChange={(e) => setUsuarioCorto(e.target.value)}
                placeholder="caja01" data-testid="op-usuario-input" autoComplete="off"
                className="h-11 rounded-none bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[#E7B93E]"
              />
            </div>

            {/* NIP display */}
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/60">NIP de acceso</Label>
              <div className="flex gap-3 mt-2 mb-4" data-testid="op-nip-display">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`h-12 flex-1 border flex items-center justify-center text-2xl font-display font-bold ${nip[i] ? "border-[#E7B93E] bg-[#E7B93E]/10" : "border-white/20"}`}>
                    {nip[i] ? "•" : ""}
                  </div>
                ))}
              </div>
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n} onClick={() => pressKey(String(n))} data-testid={`op-key-${n}`}
                  className="btn-hard h-14 bg-white/5 border border-white/20 text-white font-display text-xl font-bold hover:bg-white/10"
                >
                  {n}
                </button>
              ))}
              <button onClick={() => setNip("")} data-testid="op-key-clear" className="btn-hard h-14 bg-white/5 border border-white/20 text-white/60 text-xs font-bold uppercase tracking-wider hover:bg-white/10">C</button>
              <button onClick={() => pressKey("0")} data-testid="op-key-0" className="btn-hard h-14 bg-white/5 border border-white/20 text-white font-display text-xl font-bold hover:bg-white/10">0</button>
              <button onClick={delKey} data-testid="op-key-del" className="btn-hard h-14 bg-white/5 border border-white/20 text-white flex items-center justify-center hover:bg-white/10"><Delete className="h-5 w-5" /></button>
            </div>

            <button
              onClick={handleOperativo} disabled={opLoading} data-testid="op-login-btn"
              className="btn-hard w-full h-12 bg-[#E7B93E] text-[#0A1F38] border border-[#E7B93E] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
            >
              {opLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Entrar <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
          <p className="mt-6 text-xs text-white/50">Para roles Cajero y Valet.</p>
        </motion.div>
      </div>
    </div>
  );
}
